import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  AlertCircle, CheckCircle, CheckSquare, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp,
  RefreshCw, Plus, Trash2, Edit2, ChevronRight, Search, X, Copy, Activity, FileText,
  Zap, Building2, User, Filter, Mail, Send,
} from 'lucide-react';
import * as api from '../services/api';
/* G92 — re-skin with v2 chrome. v1 logic, hooks, handlers, and API calls
 * are preserved verbatim; only the wrapper, header, summary tiles, and
 * tab bar are swapped to v2 components. v2 imports are aliased to avoid
 * colliding with v1's local Modal component (`common/Modal`). */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Button as V2Button,
  Pill as V2Pill,
  MetricCard as V2MetricCard,
} from '../components/v2';

// ---------------------------------------------------------------------------
// STYLES
// ---------------------------------------------------------------------------
const card = {
  background: '#ffffff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 20, marginBottom: 20,
};
const h2Style = { fontSize: 20, fontWeight: 700, color: '#18181b', margin: '0 0 8px 0' };
const h3Style = { fontSize: 16, fontWeight: 600, color: '#18181b', margin: '0 0 16px 0' };
const subtitleStyle = { fontSize: 13, color: '#71717a', margin: '0 0 24px 0' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#52525b', marginBottom: 8 };
const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: '1px solid #e4e4e7', fontSize: 13, boxSizing: 'border-box',
};
const selectStyle = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid #e4e4e7',
  background: '#ffffff', fontSize: 13, boxSizing: 'border-box',
};
const btnBase = {
  padding: '6px 12px', borderRadius: 6, border: 'none', fontSize: 12,
  fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
};
const btnPrimary = { ...btnBase, background: '#0d9488', color: '#fff' };
const btnSecondary = { ...btnBase, background: '#f5f5f4', color: '#57534e', border: '1px solid #a8a29e' };
const btnSuccess = { ...btnBase, background: '#059669', color: '#fff' };
const btnDanger = { ...btnBase, background: '#dc2626', color: '#fff' };
const btnWarning = { ...btnBase, background: '#d97706', color: '#fff' };
const thStyle = {
  padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e4e4e7',
};
const tdStyle = { padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #f5f5f4' };

const tabBarStyle = {
  display: 'flex', gap: 0, borderBottom: '2px solid #e4e4e7', marginBottom: 24,
};

const tabStyle = (active) => ({
  padding: '12px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  color: active ? '#0d9488' : '#71717a',
  borderBottom: active ? '2px solid #0d9488' : '2px solid transparent',
  marginBottom: '-2px', display: 'inline-flex', alignItems: 'center', gap: 8,
  whiteSpace: 'nowrap',
});

const summaryCardStyle = {
  background: '#ffffff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 16, flex: 1, minWidth: 200,
};

const summaryLabelStyle = { fontSize: 12, color: '#71717a', fontWeight: 600, marginBottom: 8 };
const summaryValueStyle = { fontSize: 24, fontWeight: 700, color: '#18181b' };

// Status colors
const STATUS_COLORS = {
  pending: '#d97706',
  awaiting_second: '#8b5cf6',
  approved: '#059669',
  rejected: '#dc2626',
  escalated: '#ea580c',
  expired: '#6b7280',
  cancelled: '#a1a1aa',
};

const URGENCY_COLORS = {
  low: '#10b981',
  normal: '#f59e0b',
  high: '#f97316',
  critical: '#dc2626',
};

const REQUEST_TYPE_COLORS = {
  skill_execution: '#059669',
  workflow_step: '#8b5cf6',
  agent_action: '#d97706',
  phase_advance: '#0ea5e9',
};

// Avatar component (borrowed from TeamPage pattern)
function Avatar({ user, size = 32 }) {
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const colors = ['#fee2e2', '#e3f4f1', '#f5f5f4', '#fef3f2'];
  const idx = (user?.id?.charCodeAt(0) || 0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: colors[idx],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600, color: '#57534e',
    }}>
      {initials}
    </div>
  );
}

// Urgency Dot
function UrgencyDot({ urgency }) {
  const color = URGENCY_COLORS[urgency] || URGENCY_COLORS.normal;
  return <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />;
}

// Status Badge
function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#a8a29e';
  const labelMap = {
    pending: 'Pending',
    awaiting_second: 'Awaiting 2nd',
    approved: 'Approved',
    rejected: 'Rejected',
    escalated: 'Escalated',
    expired: 'Expired',
    cancelled: 'Cancelled',
  };
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: 4, fontSize: 11,
      fontWeight: 600, background: color, color: '#fff',
    }}>
      {labelMap[status] || status}
    </span>
  );
}

// Request Type Badge
function RequestTypeBadge({ type }) {
  const color = REQUEST_TYPE_COLORS[type] || '#a8a29e';
  const labelMap = {
    skill_execution: 'Skill',
    workflow_step: 'Workflow',
    agent_action: 'Agent',
    phase_advance: 'Phase',
  };
  return (
    <span style={{
      display: 'inline-block', padding: '4px 8px', borderRadius: 3, fontSize: 11,
      fontWeight: 600, background: color, color: '#fff',
    }}>
      {labelMap[type] || type}
    </span>
  );
}

// Approval Type Badge (Single/Dual)
function ApprovalTypeBadge({ approvalType }) {
  const isSingle = approvalType === 'single';
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: 4, fontSize: 11,
      fontWeight: 600, background: isSingle ? '#3b82f6' : '#8b5cf6', color: '#fff',
    }}>
      {isSingle ? 'Single' : 'Dual'}
    </span>
  );
}

// Request Detail Panel
function DetailPanel({ request, onClose, onApprove, onReject, canApprove, isLoading }) {
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [fullRequest, setFullRequest] = useState(null);
  // Dynamic form state — captures the operator's answers to whatever
  // formSchema the request carries. Bridged HUMAN tasks (Conductor's
  // take_claim, etc.) ship a schema that we render below; native
  // approvals leave formSchema null and the comment box is enough.
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState(null);
  const pgAudit = usePaginationState(10);

  useEffect(() => {
    if (!request?.id) return;
    const loadDetails = async () => {
      setDetailLoading(true);
      try {
        // Use enriched endpoint for hydrated user/agent/workflow info
        const detailRes = await api.getEnrichedApprovalRequest(request.id);
        const detail = detailRes?.data || detailRes;
        setFullRequest(detail);
      } catch (e) { console.error('Failed to load request detail', e); }
      setDetailLoading(false);
    };
    const loadAudit = async () => {
      setAuditLoading(true);
      try {
        const auditRes = await api.getApprovalAuditLog(request.id);
        const logs = auditRes?.data || auditRes;
        setAuditLog(Array.isArray(logs) ? logs : []);
      } catch (e) { console.error('Failed to load audit log', e); }
      setAuditLoading(false);
    };
    loadDetails();
    loadAudit();
  }, [request?.id]);

  const visibleAudit = pgAudit.paginate(auditLog);

  const requestInfo = fullRequest || request;

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 500, maxWidth: '100%',
      background: '#ffffff', borderLeft: '1px solid #e4e4e7', overflowY: 'auto',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.1)', zIndex: 1000,
      animation: 'slideIn 0.3s ease-out',
    }}>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {/* Header */}
      <div style={{ padding: '20px', borderBottom: '1px solid #e4e4e7', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h3 style={{ ...h3Style, margin: '0 0 8px 0' }}>{requestInfo?.title || 'Request Details'}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <RequestTypeBadge type={requestInfo?.type} />
            <StatusBadge status={requestInfo?.status} />
            <UrgencyDot urgency={requestInfo?.urgency} />
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <X size={20} />
        </button>
      </div>

      {detailLoading ? (
        <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>
      ) : (
        <div style={{ padding: 20 }}>
          {/* Request Info Grid */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Request Info</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Requested By</div>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar user={requestInfo?.requestedBy} size={24} />
                  {requestInfo?.requestedBy?.firstName} {requestInfo?.requestedBy?.lastName}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Requested At</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {requestInfo?.requestedAt ? new Date(requestInfo.requestedAt).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Assigned To</div>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {requestInfo?.assignedTo ? (
                    <>
                      <Avatar user={requestInfo.assignedTo} size={24} />
                      {requestInfo.assignedTo.firstName} {requestInfo.assignedTo.lastName}
                    </>
                  ) : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Approval Type</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  <ApprovalTypeBadge approvalType={requestInfo?.approvalType} />
                </div>
              </div>
            </div>
          </div>

          {/* Source & Agent Info */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Source</h4>
            <div style={{
              padding: 12, background: '#fafaf9', borderRadius: 6, border: '1px solid #e7e5e4',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={16} style={{ color: '#0d9488' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {requestInfo?.source?.name || requestInfo?.sourceName || 'Unknown Skill'}
                </span>
                {requestInfo?.requestType && (
                  <span style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 3,
                    background: '#e0f2fe', color: '#0369a1',
                  }}>
                    {requestInfo.requestType.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              {requestInfo?.source?.agentName && (
                <div style={{ fontSize: 12, color: '#57534e', display: 'flex', gap: 12 }}>
                  <span><strong>Agent:</strong> {requestInfo.source.agentName}</span>
                  {requestInfo.source.agentPhase && (
                    <span style={{
                      fontSize: 10, padding: '1px 5px', borderRadius: 3,
                      background: requestInfo.source.agentPhase === 'act' ? '#dcfce7' : '#fef9c3',
                      color: requestInfo.source.agentPhase === 'act' ? '#166534' : '#854d0e',
                    }}>{requestInfo.source.agentPhase}</span>
                  )}
                  {requestInfo.source.agentStatus && (
                    <span style={{
                      fontSize: 10, padding: '1px 5px', borderRadius: 3,
                      background: requestInfo.source.agentStatus === 'active' ? '#dcfce7' : '#fee2e2',
                      color: requestInfo.source.agentStatus === 'active' ? '#166534' : '#991b1b',
                    }}>{requestInfo.source.agentStatus}</span>
                  )}
                </div>
              )}
              {requestInfo?.description && (
                <div style={{ fontSize: 12, color: '#78716c', marginTop: 4 }}>
                  {requestInfo.description}
                </div>
              )}
            </div>
          </div>

          {/* Execution Details */}
          {requestInfo?.executionDetails && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Execution Details</h4>
              <div style={{
                padding: 12, background: '#fafaf9', borderRadius: 6, border: '1px solid #e7e5e4',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {requestInfo.executionDetails.skillName && (
                    <div>
                      <div style={{ fontSize: 10, color: '#a8a29e', textTransform: 'uppercase' }}>Skill</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{requestInfo.executionDetails.skillName}</div>
                    </div>
                  )}
                  {requestInfo.executionDetails.workflowName && (
                    <div>
                      <div style={{ fontSize: 10, color: '#a8a29e', textTransform: 'uppercase' }}>Workflow</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{requestInfo.executionDetails.workflowName}</div>
                    </div>
                  )}
                  {requestInfo.executionDetails.status && (
                    <div>
                      <div style={{ fontSize: 10, color: '#a8a29e', textTransform: 'uppercase' }}>Execution Status</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{requestInfo.executionDetails.status}</div>
                    </div>
                  )}
                  {requestInfo.executionDetails.workflowRunStatus && (
                    <div>
                      <div style={{ fontSize: 10, color: '#a8a29e', textTransform: 'uppercase' }}>Workflow Run</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{requestInfo.executionDetails.workflowRunStatus}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Input / Context Data — workflow_step approvals carry an
              `upstream` sub-object with the resolved upstream task
              outputs (synthesized by the Temporal OSS activity that mints
              the request, see DAGNodeActivitiesImpl.buildLegibleContextData).
              We render that bucket as labeled rows; everything else
              falls back to the simple-object / JSON path. Native
              workflow plumbing (orgId, sourceType, inputMapping
              templates, etc.) is filtered out server-side. */}
          {requestInfo?.contextData && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>
                {requestInfo?.requestType === 'skill_execution' ? 'Execution Input' : 'Upstream Context'}
              </h4>
              {(() => {
                const cd = requestInfo.contextData;
                const upstream = (cd && typeof cd === 'object' && cd.upstream && typeof cd.upstream === 'object')
                  ? cd.upstream : null;
                if (upstream) {
                  const entries = Object.entries(upstream);
                  if (entries.length === 0) {
                    return (
                      <div style={{ padding: 12, background: '#fafaf9', borderRadius: 6, border: '1px solid #e7e5e4', fontSize: 12, color: '#78716c' }}>
                        No upstream context — this gate is the first step in the workflow.
                      </div>
                    );
                  }
                  // Humanize camelCase / snake_case keys for the label column:
                  // "customerName" → "Customer Name", "policy_number" → "Policy Number".
                  // Backend (buildLegibleContextData) already strips framework
                  // plumbing so every entry here is real business data the
                  // approver needs to see.
                  const humanizeKey = (k) => k
                    .replace(/[_-]+/g, ' ')
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/\b\w/g, (c) => c.toUpperCase())
                    .trim();
                  // Render an array of scalars as "a, b, c" rather than JSON.
                  // Render booleans as Yes/No for legibility.
                  const renderValue = (v) => {
                    if (Array.isArray(v) && v.every((x) => x == null || ['string', 'number', 'boolean'].includes(typeof x))) {
                      return v.join(', ');
                    }
                    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
                    if (v == null) return '—';
                    return typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v);
                  };
                  return (
                    <div style={{ padding: 12, background: '#fafaf9', borderRadius: 6, border: '1px solid #e7e5e4', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {entries.map(([key, value]) => {
                        const isComplex = typeof value === 'object' && value !== null
                          && !(Array.isArray(value) && value.every((x) => x == null || ['string', 'number', 'boolean'].includes(typeof x)));
                        const display = renderValue(value);
                        return (
                          <div key={key} style={{ display: 'flex', gap: 8, fontSize: 13, alignItems: 'flex-start' }}>
                            <span style={{ fontWeight: 600, minWidth: 160, color: '#57534e', flexShrink: 0 }}>{humanizeKey(key)}</span>
                            {isComplex ? (
                              <pre style={{ margin: 0, color: '#1c1917', fontSize: 12, fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>{display}</pre>
                            ) : (
                              <span style={{ color: '#1c1917', wordBreak: 'break-word', flex: 1 }}>{display}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                // Legacy path — small object as labeled rows, otherwise JSON.
                if (typeof cd === 'object' && !Array.isArray(cd) && Object.keys(cd).length <= 10) {
                  return (
                    <div style={{ padding: 12, background: '#fafaf9', borderRadius: 6, border: '1px solid #e7e5e4', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {Object.entries(cd).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                          <span style={{ fontWeight: 600, minWidth: 100, color: '#57534e' }}>{key}:</span>
                          <span style={{ color: '#1c1917', wordBreak: 'break-all' }}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <pre style={{ background: '#f5f5f4', padding: 12, borderRadius: 6, fontSize: 11, overflow: 'auto', maxHeight: 200, border: '1px solid #e4e4e7' }}>
                    {JSON.stringify(cd, null, 2)}
                  </pre>
                );
              })()}
            </div>
          )}

          {/* Decision Timeline */}
          {(requestInfo?.approvalType === 'dual' || requestInfo?.decision) && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Decision Timeline</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {requestInfo?.decision && (
                  <div style={{ padding: 12, background: '#e3f4f1', borderRadius: 6, borderLeft: `4px solid ${requestInfo.decision.decision === 'approved' ? '#059669' : '#dc2626'}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                      {requestInfo.decision.decision === 'approved' ? 'Approved' : 'Rejected'}
                    </div>
                    <div style={{ fontSize: 11, color: '#57534e', marginBottom: 8 }}>
                      {requestInfo.decision.decidedBy?.firstName} {requestInfo.decision.decidedBy?.lastName} • {requestInfo.decision.decidedAt ? new Date(requestInfo.decision.decidedAt).toLocaleString() : '—'}
                    </div>
                    {requestInfo.decision.comment && (
                      <div style={{ fontSize: 11, color: '#57534e', fontStyle: 'italic' }}>"{requestInfo.decision.comment}"</div>
                    )}
                  </div>
                )}
                {requestInfo?.approvalType === 'dual' && requestInfo?.secondDecision && (
                  <div style={{ padding: 12, background: '#e3f4f1', borderRadius: 6, borderLeft: `4px solid ${requestInfo.secondDecision.decision === 'approved' ? '#059669' : '#dc2626'}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                      Second Approval: {requestInfo.secondDecision.decision === 'approved' ? 'Approved' : 'Rejected'}
                    </div>
                    <div style={{ fontSize: 11, color: '#57534e', marginBottom: 8 }}>
                      {requestInfo.secondDecision.decidedBy?.firstName} {requestInfo.secondDecision.decidedBy?.lastName} • {requestInfo.secondDecision.decidedAt ? new Date(requestInfo.secondDecision.decidedAt).toLocaleString() : '—'}
                    </div>
                    {requestInfo.secondDecision.comment && (
                      <div style={{ fontSize: 11, color: '#57534e', fontStyle: 'italic' }}>"{requestInfo.secondDecision.comment}"</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Log */}
          {auditLog.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Audit Log</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, padding: '8px 12px' }}>Action</th>
                    <th style={{ ...thStyle, padding: '8px 12px' }}>Actor</th>
                    <th style={{ ...thStyle, padding: '8px 12px' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAudit.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ ...tdStyle, padding: '8px 12px' }}>{entry.action}</td>
                      <td style={{ ...tdStyle, padding: '8px 12px' }}>{entry.actor?.firstName ? `${entry.actor.firstName} ${entry.actor.lastName || ''}`.trim() : (entry.actorRole || entry.actorId?.substring(0, 8) || 'System')}</td>
                      <td style={{ ...tdStyle, padding: '8px 12px' }}>{(entry.createdAt || entry.timestamp) ? new Date(entry.createdAt || entry.timestamp).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                totalItems={auditLog.length}
                page={pgAudit.page}
                pageSize={pgAudit.pageSize}
                onPageChange={pgAudit.setPage}
                onPageSizeChange={pgAudit.setPageSize}
                noun="log entries"
              />
            </div>
          )}

          {/* Form fields (bridged HUMAN tasks) — rendered above the comment
              box so operators see the form they must fill out before
              approving. Reject ignores formData; we still keep the form
              visible so they can see what was being asked. */}
          {canApprove && requestInfo?.status === 'pending'
            && requestInfo?.formSchema?.fields?.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>
                Form (required for approval)
              </h4>
              {(requestInfo.formSchema.fields || []).map((field) => {
                const val = formData[field.name] ?? '';
                const onChange = (e) => {
                  setFormData({ ...formData, [field.name]: e.target.value });
                  setFormError(null);
                };
                const labelEl = (
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#3f3f46', marginBottom: 6 }}>
                    {field.label || field.name}
                    {field.required ? <span style={{ color: '#dc2626' }}> *</span> : null}
                  </label>
                );
                if (field.type === 'select') {
                  return (
                    <div key={field.name} style={{ marginBottom: 12 }}>
                      {labelEl}
                      <select
                        value={val}
                        onChange={onChange}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      >
                        <option value="">— Select —</option>
                        {(field.options || []).map((opt) => (
                          <option key={String(opt.value)} value={opt.value}>
                            {opt.label || String(opt.value)}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                if (field.type === 'textarea') {
                  return (
                    <div key={field.name} style={{ marginBottom: 12 }}>
                      {labelEl}
                      <textarea
                        value={val}
                        onChange={onChange}
                        placeholder={field.placeholder || ''}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 13, minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                    </div>
                  );
                }
                // Default: single-line text input
                return (
                  <div key={field.name} style={{ marginBottom: 12 }}>
                    {labelEl}
                    <input
                      type="text"
                      value={val}
                      onChange={onChange}
                      placeholder={field.placeholder || ''}
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>
                );
              })}
              {formError && (
                <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{formError}</div>
              )}
            </div>
          )}

          {/* Comment & Action */}
          {canApprove && requestInfo?.status === 'pending' && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#71717a', marginBottom: 12, textTransform: 'uppercase' }}>Your Comment</h4>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                style={{
                  width: '100%', padding: 12, borderRadius: 6, border: '1px solid #e4e4e7',
                  fontSize: 13, minHeight: 80, boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => {
                    // Validate required fields before firing approve.
                    const fields = requestInfo?.formSchema?.fields || [];
                    const missing = fields
                      .filter((f) => f.required && !String(formData[f.name] ?? '').trim())
                      .map((f) => f.label || f.name);
                    if (missing.length > 0) {
                      setFormError(`Please fill: ${missing.join(', ')}`);
                      return;
                    }
                    onApprove(request.id, comment, formData);
                  }}
                  disabled={isLoading}
                  style={{ ...btnSuccess, opacity: isLoading ? 0.6 : 1 }}
                >
                  <CheckCircle size={14} /> Approve
                </button>
                <button
                  onClick={() => onReject(request.id, comment)}
                  disabled={isLoading}
                  style={{ ...btnDanger, opacity: isLoading ? 0.6 : 1 }}
                >
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Policy Modal
function PolicyModal({ isOpen, onClose, onSave, isLoading, users, roles = [] }) {
  const [form, setForm] = useState({
    targetType: 'agent',
    targetId: '',
    approvalType: 'single',
    approverStrategy: 'hierarchical',
    specificUserId: '',
    specificRole: '',
    escalationEnabled: false,
    escalationTimeoutMinutes: 60,
    maxEscalationLevels: 3,
  });

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Approval Policy">
      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          <label style={labelStyle}>Target Type</label>
          <select
            value={form.targetType}
            onChange={(e) => setForm({ ...form, targetType: e.target.value })}
            style={selectStyle}
          >
            <option value="agent">Agent</option>
            <option value="skill">Skill</option>
            <option value="workflow">Workflow</option>
            <option value="workflow_node">Workflow Node</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Target ID (Optional)</label>
          <input
            type="text"
            value={form.targetId}
            onChange={(e) => setForm({ ...form, targetId: e.target.value })}
            placeholder="Leave blank for org-wide policy"
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Approval Type</label>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={form.approvalType === 'single'}
                onChange={() => setForm({ ...form, approvalType: 'single' })}
              />
              Single
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={form.approvalType === 'dual'}
                onChange={() => setForm({ ...form, approvalType: 'dual' })}
              />
              Dual
            </label>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Approver Strategy</label>
          <select
            value={form.approverStrategy}
            onChange={(e) => setForm({ ...form, approverStrategy: e.target.value })}
            style={selectStyle}
          >
            <option value="hierarchical">Hierarchical</option>
            <option value="specific_user">Specific User</option>
            <option value="specific_role">Specific Role</option>
          </select>
        </div>

        {form.approverStrategy === 'specific_user' && (
          <div>
            <label style={labelStyle}>Approver User</label>
            <select
              value={form.specificUserId}
              onChange={(e) => setForm({ ...form, specificUserId: e.target.value })}
              style={selectStyle}
            >
              <option value="">Select user...</option>
              {users && users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {form.approverStrategy === 'specific_role' && (
          <div>
            <label style={labelStyle}>Approver Role</label>
            <select
              value={form.specificRole}
              onChange={(e) => setForm({ ...form, specificRole: e.target.value })}
              style={selectStyle}
            >
              <option value="">Select role...</option>
              <option value="Tenant Admin">Tenant Admin</option>
              <option value="Org Admin">Org Admin</option>
              <option value="Super Admin">Super Admin</option>
            </select>
          </div>
        )}

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.escalationEnabled}
              onChange={(e) => setForm({ ...form, escalationEnabled: e.target.checked })}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#52525b' }}>Enable Escalation</span>
          </label>
        </div>

        {form.escalationEnabled && (
          <>
            <div>
              <label style={labelStyle}>Escalation Timeout (minutes)</label>
              <input
                type="number"
                value={form.escalationTimeoutMinutes}
                onChange={(e) => setForm({ ...form, escalationTimeoutMinutes: parseInt(e.target.value) })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Max Escalation Levels</label>
              <input
                type="number"
                value={form.maxEscalationLevels}
                onChange={(e) => setForm({ ...form, maxEscalationLevels: parseInt(e.target.value) })}
                style={inputStyle}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={btnSecondary} disabled={isLoading}>
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            style={{ ...btnPrimary, opacity: isLoading ? 0.6 : 1 }}
            disabled={isLoading}
          >
            Create Policy
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Main Component
export default function ApprovalCenterPage() {
  const {
    approvalInbox, approvalSubmitted, approvalAll, approvalPolicies, approvalStats, approvalInboxCount,
    loadApprovalInbox, loadApprovalInboxCount, loadApprovalSubmitted, loadApprovalAll,
    loadApprovalPolicies, loadApprovalStats,
    approveApprovalRequest, rejectApprovalRequest, cancelApprovalRequest,
    createApprovalPolicy, updateApprovalPolicy, deleteApprovalPolicy,
    users, loadUsers,
  } = usePlatform();
  const { user: authUser, session, hasPermission } = useAuth();
  const { addToast } = useToast();

  const [tab, setTab] = useState('inbox');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination for each tab
  const pgInbox = usePaginationState(10);
  const pgSubmitted = usePaginationState(10);
  const pgAll = usePaginationState(10);
  const pgPolicies = usePaginationState(10);

  // Permissions
  const isTenantAdmin = hasPermission('tenant.manage') || session?.role?.includes('Admin');
  const canManagePolicies = isTenantAdmin;

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  // Load data on mount
  useEffect(() => {
    loadApprovalInbox();
    loadApprovalInboxCount();
    loadApprovalSubmitted();
    loadApprovalStats({ orgId, tenantId });
    loadApprovalPolicies({ orgId, tenantId });
    loadUsers({ tenantId });
  }, [loadApprovalInbox, loadApprovalInboxCount, loadApprovalSubmitted, loadApprovalStats, loadApprovalPolicies, loadUsers, orgId, tenantId]);

  // Load all requests when All tab is opened
  useEffect(() => {
    if (tab === 'all') {
      loadApprovalAll({ orgId, tenantId, status: statusFilter === 'all' ? undefined : statusFilter });
    }
  }, [tab, statusFilter, loadApprovalAll, orgId, tenantId]);

  // Handlers
  const handleApprove = async (requestId, comment, formData) => {
    setLoading(true);
    const result = await approveApprovalRequest(requestId, comment, formData);
    setLoading(false);
    if (result?.ok) {
      addToast('Request approved', 'success');
      setShowDetailPanel(false);
      setSelectedRequest(null);
      loadApprovalInbox();
      loadApprovalInboxCount();
    } else {
      addToast(`Approval failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  const handleReject = async (requestId, comment) => {
    setLoading(true);
    const result = await rejectApprovalRequest(requestId, comment);
    setLoading(false);
    if (result?.ok) {
      addToast('Request rejected', 'success');
      setShowDetailPanel(false);
      setSelectedRequest(null);
      loadApprovalInbox();
      loadApprovalInboxCount();
    } else {
      addToast(`Rejection failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  const handleCancel = async (requestId) => {
    setLoading(true);
    const result = await cancelApprovalRequest(requestId);
    setLoading(false);
    if (result?.ok) {
      addToast('Request cancelled', 'success');
      loadApprovalSubmitted();
      loadApprovalInboxCount();
    } else {
      addToast(`Cancellation failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  const handleCreatePolicy = async (form) => {
    setPolicyLoading(true);
    const result = await createApprovalPolicy(form);
    setPolicyLoading(false);
    if (result?.ok) {
      addToast('Policy created', 'success');
      setShowPolicyModal(false);
      loadApprovalPolicies({ orgId, tenantId });
    } else {
      addToast(`Policy creation failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    const result = await deleteApprovalPolicy(policyId);
    if (result?.ok) {
      addToast('Policy deleted', 'success');
      loadApprovalPolicies({ orgId, tenantId });
    } else {
      addToast(`Deletion failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // Visible data
  const visibleInbox = pgInbox.paginate(approvalInbox || []);
  const visibleSubmitted = pgSubmitted.paginate(approvalSubmitted || []);
  const visibleAll = pgAll.paginate(approvalAll || []);
  const visiblePolicies = pgPolicies.paginate(approvalPolicies || []);

  /* G92 — v2 TabNav config. The 'all' and 'policies' tabs are admin-only;
   * we filter them out of the array (instead of rendering disabled) so
   * the tab bar matches v1 semantics exactly. */
  const v2Tabs = [
    { id: 'inbox', label: 'My Inbox', badge: approvalInboxCount > 0 ? approvalInboxCount : undefined },
    { id: 'submitted', label: 'Submitted', badge: (approvalSubmitted?.length || 0) > 0 ? approvalSubmitted.length : undefined },
    ...(isTenantAdmin ? [
      { id: 'all', label: 'All Requests', badge: (approvalAll?.length || 0) > 0 ? approvalAll.length : undefined },
      { id: 'policies', label: 'Policies' },
    ] : []),
  ];

  const handleTabChange = (id) => {
    setTab(id);
    if (id === 'inbox')      pgInbox.resetPage();
    if (id === 'submitted')  pgSubmitted.resetPage();
    if (id === 'all')        pgAll.resetPage();
    if (id === 'policies')   pgPolicies.resetPage();
  };

  return (
    <V2PageShell
      title="Approval Center"
      subtitle="Review and manage approval requests across agents, skills, and workflows."
      tags={<V2Pill tone="cyan" size="sm">Operate</V2Pill>}
      actions={
        <V2Button
          variant="ghost"
          size="sm"
          iconLeft={<RefreshCw size={13} />}
          onClick={() => {
            loadApprovalInbox();
            loadApprovalInboxCount();
            loadApprovalSubmitted();
            loadApprovalStats({ orgId, tenantId });
            addToast('Refreshed', 'success');
          }}
        >
          Refresh
        </V2Button>
      }
    >
      {/* Summary tiles — v2 MetricCards keep the same data and tones */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <V2MetricCard label="Pending"   value={approvalStats?.pending   ?? 0} tone="warning" />
        <V2MetricCard label="Approved"  value={approvalStats?.approved  ?? 0} tone="success" />
        <V2MetricCard label="Rejected"  value={approvalStats?.rejected  ?? 0} tone="danger"  />
        <V2MetricCard label="Escalated" value={approvalStats?.escalated ?? 0} tone="warning" />
      </div>

      {/* v2 TabNav */}
      <V2TabNav
        tabs={v2Tabs}
        activeId={tab}
        onChange={handleTabChange}
        ariaLabel="Approval Center sections"
      />

      <div className="my-4" />


      {/* TAB: My Inbox */}
      {tab === 'inbox' && (
        <div style={card}>
          {approvalInbox && approvalInbox.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>URGENCY</th>
                    <th style={thStyle}>TITLE</th>
                    <th style={thStyle}>TYPE</th>
                    <th style={thStyle}>SOURCE</th>
                    <th style={thStyle}>REQUESTED BY</th>
                    <th style={thStyle}>REQUESTED AT</th>
                    <th style={thStyle}>STATUS</th>
                    <th style={thStyle}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleInbox.map((req) => (
                    <tr key={req.id} onClick={() => { setSelectedRequest(req); setShowDetailPanel(true); }}>
                      <td style={tdStyle}>
                        <UrgencyDot urgency={req.urgency} />
                      </td>
                      <td style={tdStyle}>{req.title}</td>
                      <td style={tdStyle}>
                        <RequestTypeBadge type={req.type} />
                      </td>
                      <td style={tdStyle}>{req.source?.name || '—'}</td>
                      <td style={tdStyle} title={req.requestedBy?.email}>
                        {req.requestedBy?.firstName} {req.requestedBy?.lastName}
                      </td>
                      <td style={tdStyle}>
                        {req.requestedAt ? new Date(req.requestedAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={req.status} />
                      </td>
                      <td style={tdStyle}>
                        {req.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(req.id, ''); }}
                              style={{ ...btnSuccess, padding: '4px 8px', fontSize: 11 }}
                              disabled={loading}
                            >
                              Approve
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReject(req.id, ''); }}
                              style={{ ...btnDanger, padding: '4px 8px', fontSize: 11 }}
                              disabled={loading}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                totalItems={approvalInbox.length}
                page={pgInbox.page}
                pageSize={pgInbox.pageSize}
                onPageChange={pgInbox.setPage}
                onPageSizeChange={pgInbox.setPageSize}
                noun="requests"
              />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
              <AlertCircle size={32} style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0 }}>No pending approvals</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Submitted */}
      {tab === 'submitted' && (
        <div style={card}>
          {approvalSubmitted && approvalSubmitted.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>TITLE</th>
                    <th style={thStyle}>TYPE</th>
                    <th style={thStyle}>SOURCE</th>
                    <th style={thStyle}>REQUESTED AT</th>
                    <th style={thStyle}>STATUS</th>
                    <th style={thStyle}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSubmitted.map((req) => (
                    <tr key={req.id} onClick={() => { setSelectedRequest(req); setShowDetailPanel(true); }}>
                      <td style={tdStyle}>{req.title}</td>
                      <td style={tdStyle}>
                        <RequestTypeBadge type={req.type} />
                      </td>
                      <td style={tdStyle}>{req.source?.name || '—'}</td>
                      <td style={tdStyle}>
                        {req.requestedAt ? new Date(req.requestedAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={req.status} />
                      </td>
                      <td style={tdStyle}>
                        {req.status === 'pending' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancel(req.id); }}
                            style={{ ...btnWarning, padding: '4px 8px', fontSize: 11 }}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                totalItems={approvalSubmitted.length}
                page={pgSubmitted.page}
                pageSize={pgSubmitted.pageSize}
                onPageChange={pgSubmitted.setPage}
                onPageSizeChange={pgSubmitted.setPageSize}
                noun="requests"
              />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
              <Send size={32} style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0 }}>No submitted requests</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: All Requests */}
      {tab === 'all' && isTenantAdmin && (
        <div style={card}>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Filter size={14} style={{ color: '#71717a' }} />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); pgAll.resetPage(); }}
              style={{ ...selectStyle, minWidth: 150 }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="awaiting_second">Awaiting Second</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="escalated">Escalated</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {approvalAll && approvalAll.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>URGENCY</th>
                    <th style={thStyle}>TITLE</th>
                    <th style={thStyle}>TYPE</th>
                    <th style={thStyle}>SOURCE</th>
                    <th style={thStyle}>REQUESTED BY</th>
                    <th style={thStyle}>ASSIGNED TO</th>
                    <th style={thStyle}>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAll.map((req) => (
                    <tr key={req.id} onClick={() => { setSelectedRequest(req); setShowDetailPanel(true); }} style={{ cursor: 'pointer' }}>
                      <td style={tdStyle}>
                        <UrgencyDot urgency={req.urgency} />
                      </td>
                      <td style={tdStyle}>{req.title}</td>
                      <td style={tdStyle}>
                        <RequestTypeBadge type={req.type} />
                      </td>
                      <td style={tdStyle}>{req.source?.name || '—'}</td>
                      <td style={tdStyle} title={req.requestedBy?.email}>
                        {req.requestedBy?.firstName} {req.requestedBy?.lastName}
                      </td>
                      <td style={tdStyle} title={req.assignedTo?.email}>
                        {req.assignedTo?.firstName} {req.assignedTo?.lastName}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge status={req.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                totalItems={approvalAll.length}
                page={pgAll.page}
                pageSize={pgAll.pageSize}
                onPageChange={pgAll.setPage}
                onPageSizeChange={pgAll.setPageSize}
                noun="requests"
              />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
              <Activity size={32} style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0 }}>No requests</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Policies */}
      {tab === 'policies' && isTenantAdmin && (
        <div style={card}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ ...h3Style, margin: 0 }}>Approval Policies</h3>
            <button
              onClick={() => setShowPolicyModal(true)}
              style={btnPrimary}
            >
              <Plus size={14} /> Create Policy
            </button>
          </div>

          {approvalPolicies && approvalPolicies.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>TARGET TYPE</th>
                    <th style={thStyle}>TARGET ID</th>
                    <th style={thStyle}>APPROVAL TYPE</th>
                    <th style={thStyle}>STRATEGY</th>
                    <th style={thStyle}>ESCALATION</th>
                    <th style={thStyle}>STATUS</th>
                    <th style={thStyle}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePolicies.map((policy) => (
                    <tr key={policy.id}>
                      <td style={tdStyle}>{policy.targetType}</td>
                      <td style={tdStyle}>{policy.targetId || '(org-wide)'}</td>
                      <td style={tdStyle}>
                        <ApprovalTypeBadge approvalType={policy.approvalType} />
                      </td>
                      <td style={tdStyle}>{policy.approverStrategy}</td>
                      <td style={tdStyle}>
                        {policy.escalationEnabled ? `${policy.escalationTimeoutMinutes}m timeout` : 'Disabled'}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '4px 10px', borderRadius: 4,
                          fontSize: 11, fontWeight: 600, background: policy.enabled ? '#d1fae5' : '#f3f4f6',
                          color: policy.enabled ? '#065f46' : '#374151',
                        }}>
                          {policy.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => handleDeletePolicy(policy.id)}
                          style={{ ...btnDanger, padding: '4px 8px', fontSize: 11 }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                totalItems={approvalPolicies.length}
                page={pgPolicies.page}
                pageSize={pgPolicies.pageSize}
                onPageChange={pgPolicies.setPage}
                onPageSizeChange={pgPolicies.setPageSize}
                noun="policies"
              />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#71717a' }}>
              <FileText size={32} style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0 }}>No policies yet</p>
            </div>
          )}
        </div>
      )}

      {/* Detail Panel */}
      {showDetailPanel && selectedRequest && (
        <DetailPanel
          request={selectedRequest}
          onClose={() => setShowDetailPanel(false)}
          onApprove={handleApprove}
          onReject={handleReject}
          canApprove={true}
          isLoading={loading}
        />
      )}

      {/* Policy Modal */}
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        onSave={handleCreatePolicy}
        isLoading={policyLoading}
        users={users}
      />
    </V2PageShell>
  );
}
